import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormW2Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form W-2 instructions for Tax Year 2025"
          description="Get clear instructions for Form W-2 Box 12 codes. Learn how to fill out Form W-2 for Tax Year 2025 accurately with step-by-step guidance from TaxZerone."
          keywords=""
          canonical="resources/information-returns/form-w-2-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form W-2</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />

          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-w-2-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form W-2 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemProp="articleBody"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li>
                      <strong style={{ fontWeight: 600 }}>Form W-2</strong>{' '}
                      reports an employee's annual wages, tips, and the federal,
                      Social Security, and Medicare taxes withheld during the
                      tax year.
                    </li>

                    <li>
                      Employers must{' '}
                      <Link
                        href="/information-returns/efile-irs-form-w2/"
                        sx={{ color: '#0202ff' }}
                      >
                        file Form W-2
                      </Link>{' '}
                      with the Social Security Administration (SSA) and furnish
                      copies to employees by{' '}
                      <strong style={{ fontWeight: 600 }}>
                        January 31<sup>st</sup>
                      </strong>
                      .
                    </li>

                    <li>
                      This article provides{' '}
                      <strong style={{ fontWeight: 600 }}>
                        box-by-box filing instructions
                      </strong>
                      , covering employer and employee details, wages, tax
                      withheld, Social Security and Medicare wages/taxes, tips,
                      dependent care benefits, nonqualified plans, Box 12 codes,
                      Box 13 checkboxes, Box 14 other information, and
                      state/local tax reporting.
                    </li>

                    <li>
                      <Link
                        href="/resources/information-returns/form-w2-penalties/"
                        sx={{ color: '#0202ff' }}
                      >
                        Late filing penalties for Form W-2
                      </Link>{' '}
                      increase based on how late the form is filed, ranging from{' '}
                      <strong style={{ fontWeight: 600 }}>
                        $60 to $680 per return
                      </strong>
                      , with annual maximums depending on business size and
                      filing delay.
                    </li>

                    <li>
                      If you need additional time to file Form W-2, you can
                      request an extension by submitting Form 8809 on paper.
                      Mailed the completed form to the IRS by the original
                      filing due date.
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form W-2?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Form W-2 is a crucial tax document issued by employers to
                  their employees. It summarizes the employee&apos;s annual
                  income earned from the employer and the federal income taxes,
                  Social Security, and Medicare taxes withheld from their
                  paychecks throughout the year.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The employer electronically submits a copy of the W2 to the
                  IRS and also provides a copy to the employee for their tax
                  filing purposes.
                </Typography>
                {/* <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Rent payments</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Royalties</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Prizes and awards</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Payments to attorneys</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payments for services to unincorporated businesses
                    </ListItemText>
                  </ListItem>
                </List> */}

                <Alert
                  severity="info"
                  sx={{ mb: 4 }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Note:</b> Make sure your employees fill out and submit Form
                  W-4 so you have the necessary information to complete the W-2
                  accurately.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can file Form W-2?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Responsibility for{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    filing Form W-2
                  </Link>{' '}
                  falls solely on the employer&apos;s shoulders.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Employers use W-2 forms to report two key things:
                </Typography>

                <List sx={{ mb: 2 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Employee income and taxes withheld: This includes details
                      like total wages earned, federal income taxes deducted,
                      and Social Security and Medicare taxes (FICA) withheld.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Social Security contributions: The W-2 helps the Social
                      Security Administration (SSA) track employee&apos;s
                      earnings and contributions, ultimately determining their
                      future Social Security benefits.
                    </ListItemText>
                  </ListItem>
                </List>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Every employee who received salary, wages, or any other form
                  of compensation from their employer throughout the year will
                  get a{' '}
                  <Link
                    href="/resources/information-returns/form-w-2-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    irs W2
                  </Link>
                  . This is mandatory for full-time, part-time, and even
                  temporary workers. However, Form W-2 is not applicable to
                  independent contractors and self-employed individuals.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form W-2 due date?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Employers must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    file W2 Form
                  </Link>{' '}
                  with the SSA by January 31st of the following year. For the
                  2025 tax year, the deadline to report employee income and
                  taxes is{' '}
                  <b>
                    February 2<sup>nd</sup>, 2026
                  </b>
                  .
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        E-file today and avoid the deadline rush!
                      </Typography>
                      <Typography variant="body2">
                        Skip the last-minute scramble and e-file your W-2s now
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  Remember, January 31st is also the deadline to provide copies
                  of Form W-2 to your employees. This ensures they have the
                  necessary tax documentation for accurate reporting on their
                  tax returns.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form W-2 penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If you miss filing Form W-2 for your employees within the
                  deadline, you will start incurring penalties. Below is the
                  breakdown of{' '}
                  <Link
                    href="/resources/information-returns/form-w2-penalties/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form W-2 penalties
                  </Link>
                  :{' '}
                </Typography>

                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444' }}>
                            Days late{' '}
                          </TableCell>

                          <TableCell sx={{ color: '#444' }}>
                            Penalty per return{' '}
                          </TableCell>
                          <TableCell sx={{ color: '#444' }}>
                            Maximum penalty per year{' '}
                          </TableCell>
                          <TableCell sx={{ color: '#444' }}>
                            Maximum penalty for small businesses per year
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Within 30 days</TableCell>
                          <TableCell>$60</TableCell>
                          <TableCell>$683,000 </TableCell>
                          <TableCell>$239,000 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1</TableCell>
                          <TableCell>$130</TableCell>
                          <TableCell>$2,049,000 </TableCell>
                          <TableCell>$683,000 </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                          <TableCell>$4,098,500 </TableCell>
                          <TableCell>$1,366,000 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                          <TableCell>-</TableCell>
                          <TableCell>-</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Don&apos;t risk penalties!
                    </Typography>
                    <Typography variant="body2">
                      Be proactive! E-file your W-2 forms ahead of the deadline.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2instructions`}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form W-2 Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form W-2.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Employer information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-employer-img.webp" />
                  <source srcSet="/assets/images/w2-employer-img.png" />
                  <img
                    src="/assets/images/w2-employer-img.png"
                    loading="lazy"
                    alt="Employer details"
                    width={553}
                    height={226}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Box b: Enter the business EIN
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box c: Enter the Employer&apos;s name, address, and ZIP code
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box d: Enter the control number. It&apos;s a unique number,
                    used by many employers to internally track and organize
                    their W-2 forms. It&apos;s not required by the IRS
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Employee information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-employee-img.webp" />
                  <source srcSet="/assets/images/w2-employee-img.png" />
                  <img
                    src="/assets/images/w2-employee-img.png"
                    loading="lazy"
                    alt="Employee details"
                    width={553}
                    height={263}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Box a: Enter the employee&apos;s SSN
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box e: Enter the employee&apos;s first name and initial,
                    last name, and suffix in the respective fields
                  </li>
                  <li>Box f: Enter the employee&apos;s address and zip code</li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Wages, tips, other compensation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-1-img.webp" />
                  <source srcSet="/assets/images/w2-box-1-img.png" />
                  <img
                    src="/assets/images/w2-box-1-img.png"
                    loading="lazy"
                    alt="Box 1: Wages, tips, other compensation"
                    width={229}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Box 1 should include the total wages, tips, bonuses, prizes,
                  and other taxable compensation paid to the employee during the
                  year. DO NOT include pre-tax deductions like 401(k)
                  contributions. Key things to include:
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Total wages, bonuses, prizes, and awards
                  </li>
                  <li style={{ marginBottom: 16 }}>Noncash fringe benefits</li>
                  <li style={{ marginBottom: 16 }}>Reported tips</li>
                  <li style={{ marginBottom: 16 }}>
                    Reimbursed employee business expenses
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Insurance premiums for 2% S-corp shareholders
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Employee contributions to HSAs and MSAs
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Group-term life insurance over $50,000
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Non-job related educational assistance
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Employer-paid social security and Medicare taxes
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Roth 401(k) contributions
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Nonqualified deferred compensation distributions
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Amounts includible under section 457(f)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Split-dollar life insurance payments Insurance premiums for
                    2% S-corp shareholders
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    HSA contributions if taxable to employee
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    409A deferred comp no longer subject to risk of forfeiture
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Nonqualified moving expenses and reimbursements
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Payments to employees on active military duty
                  </li>
                  <li>Other taxable compensation like scholarships</li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Federal income tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-2-img.webp" />
                  <source srcSet="/assets/images/w2-box-2-img.png" />
                  <img
                    src="/assets/images/w2-box-2-img.png"
                    loading="lazy"
                    alt="Box 2: Federal income tax withheld"
                    width={230}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report the total federal income tax withheld from the
                  employee&apos;s wages for the year. This includes:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Federal income tax withholding
                  </li>
                  <li>
                    20% excise tax withheld on excess golden parachute payments.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Social security wages
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-3-img.webp" />
                  <source srcSet="/assets/images/w2-box-3-img.png" />
                  <img
                    src="/assets/images/w2-box-3-img.png"
                    loading="lazy"
                    alt="Box 3: Social security wages"
                    width={229}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report total wages subject to social security tax, not
                  including social security tips. Generally include:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Wages paid before deductions
                  </li>
                  <li style={{ marginBottom: 16 }}>Noncash payments</li>
                  <li style={{ marginBottom: 16 }}>
                    Reimbursed employee expenses
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Elective deferrals to retirement plans
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Designated Roth contributions
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Deferrals under section 457(f) and nonqualified plans
                  </li>
                  <li style={{ marginBottom: 16 }}>Signing bonuses</li>
                  <li style={{ marginBottom: 16 }}>
                    Group-term life insurance over $50K
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Accident/health insurance for 2% S-corp shareholders
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Contributions to MSAs and HSAs
                  </li>
                  <li style={{ marginBottom: 16 }}>SIMPLE contributions</li>
                  <li>Adoption benefits</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Do not include social security tips reported in Box 7. The
                  total of Boxes 3 and 7 cannot exceed the social security wage
                  base ($176,100 in 2025).
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  If you paid the employee share of social security tax, report
                  the gross amount.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Social security tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-4-img.webp" />
                  <source srcSet="/assets/images/w2-box-4-img.png" />
                  <img
                    src="/assets/images/w2-box-4-img.png"
                    loading="lazy"
                    alt="Box 4: Social security tax withheld"
                    width={230}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report the total social security tax withheld from the
                  employee&apos;s wages and tips.
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    This includes employee social security tax only, not the
                    employer share
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    The maximum amount in 2025 is $10,918.20 ($176,100 x 6.2%)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Only include social security tax on 2025 wages and tips
                  </li>
                  <li>
                    Report gross wages in Box 3 if you paid the employee share.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Medicare wages and tips
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-5-img.webp" />
                  <source srcSet="/assets/images/w2-box-5-img.png" />
                  <img
                    src="/assets/images/w2-box-5-img.png"
                    loading="lazy"
                    alt="Box 5: Medicare wages and tips"
                    width={229}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report total Medicare wages and tips. This includes:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    All wages and tips subject to social security tax (boxes 3
                    and 7)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    There is no wage base limit for Medicare tax
                  </li>
                  <li>
                    Enter tips that employees reported even if you couldn&apos;t
                    collect Medicare tax on them
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you paid the employee Medicare tax, report the gross amount
                  of wages.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  For government employers with employees who pay only Medicare
                  tax, enter Medicare wages in box 5
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Medicare tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-6-img.webp" />
                  <source srcSet="/assets/images/w2-box-6-img.png" />
                  <img
                    src="/assets/images/w2-box-6-img.png"
                    loading="lazy"
                    alt="Box 6: Medicare tax withheld"
                    width={230}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report the total Medicare tax withheld from the
                  employee&apos;s wages and tips.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Include only the employee&apos;s share of Medicare tax, not
                    the employer&apos;s share
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Include any Additional Medicare Tax withheld
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Only include Medicare tax for 2025 wages and tips
                  </li>
                  <li>
                    Do not include any Medicare tax paid by the employer. Report
                    the gross wages in Box 5 if you paid the employee&apos;s
                    share.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Social security tips
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-7-img.webp" />
                  <source srcSet="/assets/images/w2-box-7-img.png" />
                  <img
                    src="/assets/images/w2-box-7-img.png"
                    loading="lazy"
                    alt="Box 7: Social security tips"
                    width={229}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report tips that the employee reported to you, even if you
                  didn&apos;t collect social security tax on them.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Include all tips the employee reported
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    The total of boxes 3 and 7 cannot exceed $176,100 (the 2025
                    social security wage base)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Also report these tips in box 1 along with wages
                  </li>
                  <li>
                    Include tips from box 7 in the Medicare wages reported in
                    box 5 share.
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Allocated tips
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-8-img.webp" />
                  <source srcSet="/assets/images/w2-box-8-img.png" />
                  <img
                    src="/assets/images/w2-box-8-img.png"
                    loading="lazy"
                    alt="Box 8: Allocated tips"
                    width={230}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report allocated tips for employees at large food/beverage
                  establishments.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Only includes tips allocated by the employer, not reported
                    directly by the employee
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Do not include allocated tips in boxes 1, 3, 5 or 7
                  </li>
                  <li>Allocated tips are reported on Form 8027</li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Dependent care benefits
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-10-img.webp" />
                  <source srcSet="/assets/images/w2-box-10-img.png" />
                  <img
                    src="/assets/images/w2-box-10-img.png"
                    loading="lazy"
                    alt="Box 10: Dependent care benefits"
                    width={230}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report total dependent care benefits provided to the employee
                  under a dependent care assistance program (section 129).
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This include:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Fair market value of employer-sponsored daycare
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Amounts paid or reimbursed for dependent care under a
                    section 125 cafeteria plan
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Value of in-kind benefits provided by the employer
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Amounts paid directly to a daycare facility
                  </li>
                  <li>
                    Report all amounts paid, including those above the $5,000
                    exclusion.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Also include any dependent care benefits over the exclusion
                  limit in boxes 1, 3, and 5 as wages.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: Nonqualified plans
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-11-img.webp" />
                  <source srcSet="/assets/images/w2-box-11-img.png" />
                  <img
                    src="/assets/images/w2-box-11-img.png"
                    loading="lazy"
                    alt="Box 11: Nonqualified plans"
                    width={230}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report distributions to the employee from a nonqualified
                  deferred compensation plan or nongovernmental section 457(b)
                  plan.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    This helps the SSA determine if any part of box 1, 3, or 5
                    was earned in a prior year.
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Also report these distributions in box 1.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Only make one entry in box 11
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Do not report governmental 457(b) plan distributions here.
                    Use Form 1099-R instead.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Report deferrals that are no longer subject to risk of
                    forfeiture in boxes 3 and 5, not box 11.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Do not report current year deferrals or deferrals already in
                    boxes 3/5.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12: Codes
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-12-img.webp" />
                  <source srcSet="/assets/images/w2-box-12-img.png" />
                  <img
                    src="/assets/images/w2-box-12-img.png"
                    loading="lazy"
                    alt="Box 12: Codes"
                    width={230}
                    height={181}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report codes and amounts for all applicable items listed
                  below. The codes do not correspond to boxes 12a–12d on Form
                  W-2.
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Use box 12 to report any items A through II
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Do not report section 414(h)(2) govt plan contributions here
                    — use box 14 instead
                  </li>
                  <li>
                    Also use box 14 for other information like union dues,
                    uniform payments, etc.
                  </li>
                </ul>
                <Typography variant="body1">Codes for Box 12:</Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444' }}>Code</TableCell>

                          <TableCell sx={{ color: '#444' }}>
                            Description
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>A</TableCell>
                          <TableCell>
                            Uncollected social security tax on tips
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>B</TableCell>
                          <TableCell>
                            Uncollected Medicare tax on tips
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>C</TableCell>
                          <TableCell>
                            Taxable cost of group-term life insurance over
                            $50,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>D</TableCell>
                          <TableCell>
                            Elective deferrals to 401(k) plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>E</TableCell>
                          <TableCell>
                            Elective deferrals under 409A nonqualified plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>F</TableCell>
                          <TableCell>
                            Elective deferrals to 408(k)(6) SARSEP plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>G</TableCell>
                          <TableCell>
                            Elective deferrals and employer contributions to
                            457(b) plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>H</TableCell>
                          <TableCell>
                            Elective deferrals to 501(c)(18)(D) tax-exempt plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>J</TableCell>
                          <TableCell>Nontaxable sick pay</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>K</TableCell>
                          <TableCell>
                            20% excise tax on excess golden parachute payments
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>L</TableCell>
                          <TableCell>
                            Substantiated employee business expense
                            reimbursements
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>M</TableCell>
                          <TableCell>
                            Uncollected social security tax on taxable cost of
                            group-term life insurance over $50,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>N</TableCell>
                          <TableCell>
                            Uncollected Medicare tax on taxable cost of
                            group-term life insurance over $50,000
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>P</TableCell>
                          <TableCell>
                            Excludable moving expense reimbursements paid
                            directly to employee
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Q</TableCell>
                          <TableCell>Nontaxable combat pay</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>R</TableCell>
                          <TableCell>
                            Employer contributions to Archer MSA
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>S</TableCell>
                          <TableCell>
                            Employee salary reduction contributions to 409A
                            nonqualified plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>T</TableCell>
                          <TableCell>Adoption benefits</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>V</TableCell>
                          <TableCell>
                            Income from exercise of nonstatutory stock option(s)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>W</TableCell>
                          <TableCell>
                            Employer contributions to Health Savings Account
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>Y</TableCell>
                          <TableCell>
                            Deferrals under section 409A nonqualified plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Z</TableCell>
                          <TableCell>
                            Income under section 409A on a nonqualified deferred
                            compensation plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>AA</TableCell>
                          <TableCell>
                            Designated Roth contributions under 401(k) plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>BB</TableCell>
                          <TableCell>
                            Designated Roth contributions under 403(b) plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>DD</TableCell>
                          <TableCell>
                            Cost of employer-sponsored health coverage
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>EE</TableCell>
                          <TableCell>
                            Designated Roth contributions under governmental
                            457(b) plan
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>FF</TableCell>
                          <TableCell>
                            Permitted benefits under a qualified small employer
                            health reimbursement arrangement
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>GG</TableCell>
                          <TableCell>
                            Income from qualified equity grants under section
                            83(i)
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>HH</TableCell>
                          <TableCell>
                            Aggregate deferrals under section 83(i) elections as
                            of the close of the calendar year
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>II</TableCell>
                          <TableCell>
                            Medicaid waiver payments excluded from gross income
                            under Notice 2014-7. Report the amount of Medicaid
                            waiver payments not reported in box 1.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 13: Checkboxes
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-13-img.webp" />
                  <source srcSet="/assets/images/w2-box-13-img.png" />
                  <img
                    src="/assets/images/w2-box-13-img.png"
                    loading="lazy"
                    alt="Box 13: Checkboxes"
                    width={230}
                    height={46}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check all boxes that apply:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Statutory employee:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Check this if the worker is a statutory employee (not a
                    common-law employee)
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Applies to certain drivers, insurance agents, home workers,
                    traveling salespeople
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Statutory employees have social security/Medicare tax but no
                    income tax withholding
                  </li>
                </ul>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Retirement plan:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Check this if the employee was covered by a retirement plan
                    such as:
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    401(k), 403(b), SEP, or SIMPLE plans
                  </li>
                  <li style={{ marginBottom: 16 }}>Governmental plans</li>
                  <li style={{ marginBottom: 16 }}>
                    Section 501(c)(18) trusts
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Generally applies if employee was eligible to participate or
                    had contributions
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Do not check for nonqualified or 457(b) plans
                  </li>
                </ul>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Third-party sick pay:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Check this only if reporting sick pay paid by a third party
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Applies if you are the third party payer or the employer
                    reporting third party sick pay
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 14: Other
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-14-img.webp" />
                  <source srcSet="/assets/images/w2-box-14-img.png" />
                  <img
                    src="/assets/images/w2-box-14-img.png"
                    loading="lazy"
                    alt="Box 14: Other"
                    width={230}
                    height={113}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Other information:
                </Typography>

                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Report 100% of annual lease value of vehicle included in
                    employee&apos;s income
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Use this box to report any other information to the
                    employee, such as:
                  </li>
                  <ul style={{ marginBottom: 16 }}>
                    <li style={{ marginBottom: 16, listStyleType: 'circle' }}>
                      State disability insurance tax withheld
                    </li>
                    <li style={{ marginBottom: 16, listStyleType: 'circle' }}>
                      Union dues
                    </li>
                    <li style={{ marginBottom: 16, listStyleType: 'circle' }}>
                      Uniform payments
                    </li>
                    <li style={{ marginBottom: 16, listStyleType: 'circle' }}>
                      Health insurance premiums deducted
                    </li>{' '}
                    <li style={{ marginBottom: 16, listStyleType: 'circle' }}>
                      Nontaxable income
                    </li>
                    <li style={{ marginBottom: 16, listStyleType: 'circle' }}>
                      Educational assistance
                    </li>
                    <li style={{ marginBottom: 16, listStyleType: 'circle' }}>
                      Minister&apos;s housing allowance
                    </li>
                    <li style={{ marginBottom: 16, listStyleType: 'circle' }}>
                      Pension plan contributions:
                    </li>
                    <ul>
                      <li style={{ marginBottom: 16, listStyleType: 'square' }}>
                        Nonelective employer contributions
                      </li>
                      <li style={{ marginBottom: 16, listStyleType: 'square' }}>
                        Voluntary after-tax employee contributions
                      </li>
                      <li style={{ marginBottom: 16, listStyleType: 'square' }}>
                        Required employee contributions
                      </li>
                      <li style={{ marginBottom: 16, listStyleType: 'square' }}>
                        Employer matching contributions
                      </li>
                    </ul>
                  </ul>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 15 through 20: State and local income tax information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/w2-box-15-img.webp" />
                  <source srcSet="/assets/images/w2-box-15-img.png" />
                  <img
                    src="/assets/images/w2-box-15-img.png"
                    loading="lazy"
                    alt="Boxes 15 through 20: State and local income tax information"
                    width={700}
                    height={63}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Used to report state and local income tax information
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter two-letter state abbreviation in box 15
                  </li>

                  <li style={{ marginBottom: 16 }}>
                    Enter state employer ID number assigned by the state in box
                    16
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Report state wages in box 16
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Report state income tax withheld in box 17
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Can report info for up to 2 states and 2 localities
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Keep each state/locality separated by the broken line
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    For more than 2 states/localities, use a second Form W-2
                  </li>
                </ul>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form W-2?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You have two options: efficient e-filing or traditional paper
                  filing.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Form W-2 E-filing (recommended)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS recommends{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-filing your W-2s
                  </Link>{' '}
                  for a faster and simpler experience. Plus, you&apos;ll get
                  instant notifications when they&apos;re processed.
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  It&apos;s that quick and easy! Choose e-filing for a smooth
                  and efficient W2 online filing experience.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you choose to paper-file Form W-2, here are the steps you
                  need to follow:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download and print Form W-2 from the IRS website.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Fill out recipient details (name, address, SSN,
                    compensation) and your business info.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the completed form to the SSA.
                  </li>
                  <li>
                    Don&apos;t forget! Mail a copy to each recipient by January
                    31st.
                  </li>
                </ol>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important notes for paper filing:
                  </Typography>
                  <ul style={{ marginBottom: 10 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail early! Allow several weeks for forms to arrive on
                      time.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      Always send a copy to each recipient for their tax
                      reporting.
                    </li>
                    <li>
                      Paper filing involves printing, mailing, and tracking
                      forms. E-filing is faster, cheaper, and simpler!
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form W-2 - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Send Form W-2 and W-3 to the address below:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>By U. S. Postal Service</TableCell>
                          <TableCell>
                            Social Security Administration <br />
                            Direct Operations Center <br />
                            Wilkes-Barre, PA 18769-0001
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            By Private Delivery Service (FedEx, UPS, etc.)
                          </TableCell>
                          <TableCell>
                            Social Security Administration
                            <br />
                            Direct Operations Center
                            <br />
                            Attn: W-2 Process
                            <br />
                            1150 E. Mountain Drive
                            <br />
                            Wilkes-Barre, PA 18702-7997
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form W-2?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    E-filing your Form W-2
                  </Link>{' '}
                  takes just 3 steps! To make it even faster, gather all the
                  necessary information (like employee details and compensation
                  amounts) before you start. That way, you can zip through the
                  filing process in no time.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Information required to file Form W-2:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Employer information, such as name, address, and EIN.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Employee information, such as name, address, and SSN.
                  </li>
                  <li>Wages paid and taxes withheld</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Once you have this information ready, you can follow the steps
                  below to e-file Form W-2 using TaxZerone.
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Step 1:</b> Fill out Form W-2
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Step 2:</b> Review & transmit the return
                  </li>
                  <li>
                    <b>Step 3:</b> Send the recipient copy
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-file Form W-2 with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  When you choose to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w2/"
                    sx={{ color: '#0202ff' }}
                  >
                    e-file Form W-2
                  </Link>{' '}
                  with TaxZerone, you can:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        IRS form validations:
                      </span>{' '}
                      catch any errors before you submit.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Bulk upload:</span>{' '}
                      Upload multiple returns and finish in a flash.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Share recipient copies:
                      </span>{' '}
                      Email recipient copies instantly
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Secure access:</span>{' '}
                      Share recipient copies in a secure
                      portal—ZeroneVault—where employees can download them
                      anytime, anywhere.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Top value:</span> Enjoy
                      the industry&apos;s best price, starting at just $0.59 per
                      form.
                    </ListItemText>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file W2 Form with TaxZerone
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              File your W2 easily with TaxZerone, starting at just{' '}
              <span style={{ fontWeight: 600 }}>$2.49 per form</span>, with bulk
              filing{' '}
              <Hidden smDown>
                {' '}
                <br />
              </Hidden>
              options as low as{' '}
              <span style={{ fontWeight: 600 }}>$0.59 Per form</span>.
            </Typography>
            <Typography variant="body1" sx={{ mb: 2, color: '#333333' }}>
              You’ll also receive instant updates on your filing status once
              it’s processed.{' '}
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 600,
                color: '#333333',
                mb: 2,
                lineHeight: 1.4,
              }}
            >
              Ready to file W2 Forms online?{' '}
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2instructions`}
              >
                E-file Form W-2
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FormW2Instructions;
